import { createReducer } from '@reduxjs/toolkit';
import { IssuesState } from '../types/issues';
import { logout } from '../types';
import {
  setIssues,
  addIssue,
  updateIssueInStore,
  removeIssue,
  setIssuesLoading,
  setIssuesError,
  setFilterType,
  setFilterStatus,
  setSortBy,
  setCurrentDraftId,
  setIsEditingIssue,
  setPreviewIssueTime,
  setSearchOpen,
  setSearchQuery,
  clearIssues,
} from '../actions/issues';

const initialState: IssuesState = {
  issues: [],
  loading: false,
  error: null,
  filterType: null,
  filterStatus: 'all',
  sortBy: 'newest',
  currentDraftId: null,
  isEditingIssue: false,
  previewIssueTime: null,
  searchOpen: false,
  searchQuery: '',
};

export const issuesReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setIssues, (state, action) => {
      state.issues = action.payload;
      state.loading = false;
      state.error = null;
    })
    .addCase(addIssue, (state, action) => {
      // Add to beginning of array (newest first)
      const exists = state.issues.find((i) => i.id === action.payload.id);
      if (!exists) {
        state.issues = [action.payload, ...state.issues];
      }
    })
    .addCase(updateIssueInStore, (state, action) => {
      const { issueId, updates } = action.payload;
      const index = state.issues.findIndex((i) => i.id === issueId);
      if (index !== -1) {
        state.issues[index] = { ...state.issues[index], ...updates };
      }
    })
    .addCase(removeIssue, (state, action) => {
      state.issues = state.issues.filter((i) => i.id !== action.payload);
    })
    .addCase(setIssuesLoading, (state, action) => {
      state.loading = action.payload;
    })
    .addCase(setIssuesError, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    })
    .addCase(setFilterType, (state, action) => {
      state.filterType = action.payload;
    })
    .addCase(setFilterStatus, (state, action) => {
      state.filterStatus = action.payload;
    })
    .addCase(setSortBy, (state, action) => {
      state.sortBy = action.payload;
    })
    .addCase(setCurrentDraftId, (state, action) => {
      state.currentDraftId = action.payload;
      // Clear issues when draft changes
      if (action.payload !== state.currentDraftId) {
        state.issues = [];
        state.isEditingIssue = false;
        state.previewIssueTime = null;
        state.searchOpen = false;
        state.searchQuery = '';
      }
    })
    .addCase(setIsEditingIssue, (state, action) => {
      state.isEditingIssue = action.payload;
      // Clear any in-flight preview when edit mode toggles off
      if (!action.payload) {
        state.previewIssueTime = null;
      }
    })
    .addCase(setPreviewIssueTime, (state, action) => {
      state.previewIssueTime = action.payload;
    })
    .addCase(setSearchOpen, (state, action) => {
      state.searchOpen = action.payload;
      // Closing search clears any pending query so the next open starts fresh
      if (!action.payload) state.searchQuery = '';
    })
    .addCase(setSearchQuery, (state, action) => {
      state.searchQuery = action.payload;
    })
    .addCase(clearIssues, (state) => {
      state.issues = [];
      state.loading = false;
      state.error = null;
      state.isEditingIssue = false;
      state.previewIssueTime = null;
      state.searchOpen = false;
      state.searchQuery = '';
    })
    .addCase(logout, (state) => {
      state.issues = [];
      state.loading = false;
      state.error = null;
      state.filterType = null;
      state.filterStatus = 'all';
      state.sortBy = 'newest';
      state.currentDraftId = null;
      state.isEditingIssue = false;
      state.previewIssueTime = null;
      state.searchOpen = false;
      state.searchQuery = '';
    });
});
